import React from 'react'
import { Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { StatusBadge } from '../../../design-system/StatusBadge/StatusBadge'
import { EmptyState } from '../../../design-system/EmptyState/EmptyState'

dayjs.extend(relativeTime)

const { Text } = Typography

const ACT_ON_BEHALF_SCOPE = 'users:act-on-behalf'
const EXPIRING_SOON_DAYS = 30

export interface IApiKeysTableItem {
  id: string
  name: string
  integrationName: string | null
  prefix: string
  ownerName: string
  scopes: string[]
  lastUsedAt?: string
  expiresAt?: string
  status: string
  revocationReasonText?: string
}

export interface IApiKeysTableProps {
  keys: IApiKeysTableItem[]
  showOwner: boolean
  onOpen: (keyId: string) => void
  loading: boolean
  onCreate: () => void
}

const KeyStatus = ({ item }: { item: IApiKeysTableItem }) => {
  const { formatMessage } = useIntl()

  if (item.status === 'revoked') {
    return (
      <>
        <StatusBadge tone="danger">{formatMessage({ id: 'publicApi.keys.statusRevoked' })}</StatusBadge>
        {item.revocationReasonText && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {item.revocationReasonText}
            </Text>
          </div>
        )}
      </>
    )
  }

  if (item.status === 'expired') {
    return <StatusBadge tone="neutral">{formatMessage({ id: 'publicApi.keys.statusExpired' })}</StatusBadge>
  }

  const daysLeft = item.expiresAt ? dayjs(item.expiresAt).diff(dayjs(), 'day') : undefined
  if (daysLeft !== undefined && daysLeft < EXPIRING_SOON_DAYS) {
    return <StatusBadge tone="warning">{formatMessage({ id: 'publicApi.keys.statusExpiresIn' }, { days: Math.max(1, daysLeft) })}</StatusBadge>
  }

  return <StatusBadge tone="success">{formatMessage({ id: 'publicApi.keys.statusActive' })}</StatusBadge>
}

const ApiKeysTable = ({ keys, showOwner, onOpen, loading, onCreate }: IApiKeysTableProps) => {
  const { formatMessage, formatDate } = useIntl()

  if (!loading && keys.length === 0) {
    return (
      <EmptyState
        variant="full"
        title={formatMessage({ id: 'publicApi.keys.emptyTitle' })}
        subtitle={formatMessage({ id: 'publicApi.keys.emptyDescription' })}
        action={{ label: formatMessage({ id: 'publicApi.keys.emptyAction' }), onClick: onCreate }}
      />
    )
  }

  const columns: ColumnsType<IApiKeysTableItem> = [
    {
      title: formatMessage({ id: 'publicApi.keys.columnName' }),
      key: 'name',
      render: (_, item) => (
        <>
          <Text strong>{item.name}</Text>
          <div>
            <Text type="secondary" italic={!item.integrationName} style={{ fontSize: 12 }}>
              {item.integrationName
                ? formatMessage({ id: 'publicApi.keys.integrationName' }, { name: item.integrationName })
                : formatMessage({ id: 'publicApi.keys.noIntegrationName' })}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {`${item.prefix}…`}
            </Text>
          </div>
        </>
      ),
    },
    ...(showOwner
      ? [
          {
            title: formatMessage({ id: 'publicApi.keys.columnOwner' }),
            key: 'owner',
            dataIndex: 'ownerName',
          },
        ]
      : []),
    {
      title: formatMessage({ id: 'publicApi.keys.columnPermissions' }),
      key: 'permissions',
      render: (_, item) => (
        <>
          <div>{formatMessage({ id: 'publicApi.keys.permissionCount' }, { count: item.scopes.length })}</div>
          {item.scopes.includes(ACT_ON_BEHALF_SCOPE) && <StatusBadge tone="warning">{formatMessage({ id: 'publicApi.keys.actsOnBehalfTag' })}</StatusBadge>}
        </>
      ),
    },
    {
      title: formatMessage({ id: 'publicApi.keys.columnLastUsed' }),
      key: 'lastUsed',
      render: (_, item) => (item.lastUsedAt ? dayjs(item.lastUsedAt).fromNow() : <Text type="secondary">{formatMessage({ id: 'publicApi.keys.neverUsed' })}</Text>),
    },
    {
      title: formatMessage({ id: 'publicApi.keys.columnExpires' }),
      key: 'expires',
      render: (_, item) => (item.expiresAt ? formatDate(item.expiresAt, { dateStyle: 'medium' }) : formatMessage({ id: 'publicApi.keys.neverExpires' })),
    },
    {
      title: formatMessage({ id: 'publicApi.keys.columnStatus' }),
      key: 'status',
      render: (_, item) => <KeyStatus item={item} />,
    },
  ]

  return (
    <Table<IApiKeysTableItem>
      rowKey="id"
      columns={columns}
      dataSource={keys}
      loading={loading}
      pagination={false}
      onRow={(item) => ({
        onClick: () => onOpen(item.id),
        style: { cursor: 'pointer', opacity: item.status === 'revoked' ? 0.6 : 1 },
      })}
    />
  )
}

export default ApiKeysTable
